import * as React from 'react';
import { View } from 'react-native';
import { useStyles } from 'react-native-unistyles';

import type { Discount as DiscountType } from '@/api';
import {
  type CheckDiscountResponse,
  useCheckDiscount,
} from '@/api/discounts/use-check-discount';
import type { TxKeyPath } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, Card, Checkbox, Input, Text } from '@/ui';

import { Icon } from '../icon';
import { Checkmark } from '../icons/checkmark';

interface DiscountProps {
  checkboxEnabled?: boolean;
  checkboxTitle?: string;
  onCheck?: (checked: boolean) => void;
  title: string;
  inputLabel: string;
  inputPlaceholder?: string;
  buttonTx: TxKeyPath;
  onPress: () => void;
  onSuccess: (discount: DiscountType) => void;
  disabled?: boolean;
  available?: boolean;
  onChangeText?: (text: string) => void;
  defaultText?: string;
}

export const Discount = ({
  checkboxEnabled = false,
  checkboxTitle,
  onCheck,
  title,
  inputLabel,
  inputPlaceholder,
  buttonTx,
  onPress,
  onSuccess,
  disabled = false,
  available = true,
  onChangeText,
  defaultText,
}: DiscountProps) => {
  const { theme } = useStyles();
  const [text, setText] = React.useState(defaultText || '');
  const [discount, setDiscount] = React.useState<DiscountType | null>(null);
  const [checked, setChecked] = React.useState(false);

  const trackDiscountRedemption = React.useCallback(
    (code: string, success: boolean, amount: number) => {
      mixpanelService.track('Discount Code Redeemed', {
        Code: code,
        Success: success,
        'Discount Amount': amount,
      });
    },
    []
  );

  React.useEffect(() => {
    if (defaultText) {
      setText(defaultText);

      checkDiscount(
        { code: defaultText },
        {
          onSuccess: (respData: CheckDiscountResponse) => {
            onSuccess(respData);
            setDiscount(respData);
            trackDiscountRedemption(defaultText, true, respData.amount);
          },
          onError: (e) => {
            console.log('e', e);
            trackDiscountRedemption(defaultText, false, 0);
          },
        }
      );
    }
  }, [defaultText]);

  const {
    mutate: checkDiscount,
    isSuccess: checkDiscountSuccess,
    isError: checkDiscountError,
    error: checkDiscountErrorData,
  } = useCheckDiscount();

  return (
    <View>
      <Card>
        {checkboxTitle ? (
          <View
            style={{
              borderBottomColor: theme.colors.card.default.borderColor,
              borderBottomWidth: 1,
            }}
          >
            <View style={{ padding: 16 }}>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Checkbox
                  onPress={() => {
                    setChecked(!checked);
                    onCheck?.(!checked);
                  }}
                  active={checked}
                  disabled={!checkboxEnabled}
                />

                <Text tx="checkout.applyEonCredits" type="title2" />
              </View>
            </View>
          </View>
        ) : null}

        <View style={{ padding: 16, gap: 16 }}>
          {title ? <Text type="title2">{title}</Text> : null}

          {available ? (
            <>
              <Input
                label={inputLabel}
                placeholder={inputPlaceholder}
                error={checkDiscountErrorData?.message}
                onChangeText={(t: string) => {
                  setText(t);
                  onChangeText?.(t);
                }}
                value={text}
              />

              <Button
                tx={buttonTx}
                type="primarySmall"
                onPress={() => {
                  checkDiscount(
                    { code: text },
                    {
                      onSuccess: (respData: CheckDiscountResponse) => {
                        onSuccess(respData);
                        setDiscount(respData);
                        trackDiscountRedemption(text, true, respData.amount);
                      },
                      onError: (e) => {
                        console.log('e', e);
                        trackDiscountRedemption(text, false, 0);
                      },
                    }
                  );
                }}
                style={{ width: 170, padding: 0, margin: 0 }}
                disabled={disabled}
              />

              {checkDiscountSuccess ? (
                <View
                  style={{
                    flexDirection: 'row',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <Icon
                    type="naked"
                    icon={<Checkmark />}
                    stroke={theme.colors.input.success.helperText}
                    fillColor={theme.colors.input.success.helperText}
                    width={12}
                    height={12}
                  />

                  <Text
                    style={{
                      flex: 1,
                      color: theme.colors.text.secondary,
                    }}
                    type="bodyRegular"
                  >
                    Discount code applied successfully. You saved $
                    {discount?.amount}! 💰
                  </Text>
                </View>
              ) : null}
            </>
          ) : null}
        </View>
      </Card>
    </View>
  );
};
